import { TabletButton } from '@campfire/tablet-button';
import { encodeTime } from '@campfire/hot-date';
import {
  Box,
  CardActions,
  CardContent,
  Checkbox,
  FormControlLabel,
  ListItem,
  TextField,
  Typography,
} from '@material-ui/core';
import { EditOutlined, Save } from '@material-ui/icons';
import { Field as FormikField, FieldProps as FormikFieldProps, Form, Formik } from 'formik';
import { DateTime } from 'luxon';
import React, { useMemo } from 'react';
import { Avatar } from '../../../../../../general/user-profile/components/Avatar';
import { useSnackbar } from '../../../../../../../global/config/useSnackbar';
import { useSaveIndividualSessionReportFetch } from '../../../activity-timeline-actions';
import { ActivityTimelinePastReportSession } from '../../../__generated__/ActivityTimelinePastReportSession';
import { getFieldValuesFromFormValues, getInitialFieldValueForIndividualReport } from './individual-report-helpers';
import { getFieldsValidationSchema } from './session-report-helpers';
import { SessionReportField, ReportTypeFieldInput } from './SessionReportFormV2';

type VolunteerProfile = {
  preferredName: string;
  lastName: string;
  avatarUrl: string | null;
};

type IndividualSessionReportFormProps = {
  session: ActivityTimelinePastReportSession;
  activityDate: string;
  existingFieldValues?: any[];
  onSubmit?: () => void;
  onCancel?: () => void;
  readOnly?: boolean;
  onEdit?: () => void;
  allowCICO?: boolean;
  existingAttendance?: boolean;
  existingCICO?: { checkIn: Date | ''; checkOut: Date | '' };
  volunteerProfile?: VolunteerProfile;
  volunteerId?: string;
};

export const IndividualSessionReportForm = ({
  session,
  activityDate,
  existingFieldValues,
  onSubmit,
  onCancel,
  readOnly,
  onEdit,
  allowCICO,
  existingAttendance,
  existingCICO,
  volunteerProfile,
  volunteerId,
}: IndividualSessionReportFormProps) => {
  const saveIndividualReport = useSaveIndividualSessionReportFetch();
  const { setSnackbar } = useSnackbar();

  const reportTypeFieldItems = useMemo(
    () =>
      session.reportType?.items
        .filter((item) => item.__typename === 'VOLUNTEER_ReportTypeFieldType')
        .sort((a, b) => a.order - b.order) || [],
    [session]
  );

  const initialValues = useMemo(() => {
    const fieldMap = reportTypeFieldItems.reduce<Record<string, any>>((prev, item) => {
      if (item.__typename !== 'VOLUNTEER_ReportTypeFieldType') return prev;
      return {
        ...prev,
        [item.field.fieldId]: getInitialFieldValueForIndividualReport(item, existingFieldValues),
      };
    }, {});

    return {
      fields: fieldMap,
      attended: existingAttendance ?? false,
      checkIn: existingCICO?.checkIn || ('' as Date | ''),
      checkOut: existingCICO?.checkOut || ('' as Date | ''),
    };
  }, [reportTypeFieldItems, existingFieldValues, existingAttendance, existingCICO]);

  const validationSchema = useMemo(() => getFieldsValidationSchema(session), [session]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values: any) => {
        const fieldValues = getFieldValuesFromFormValues(values);
        const payload: any = {
          sessionId: session.sessionId,
          activityDate,
          fieldValues,
        };

        if (volunteerId) {
          payload.volunteerId = volunteerId;
        }

        if (allowCICO) {
          if (values.checkIn) {
            payload.checkIn = encodeTime(DateTime.fromJSDate(values.checkIn));
            payload.checkOut = values.checkOut ? encodeTime(DateTime.fromJSDate(values.checkOut)) : null;
          }
        } else {
          payload.attended = values.attended;
        }

        saveIndividualReport
          .run(payload)
          .then(() => {
            setSnackbar({ open: true, message: 'Individual report submitted', variant: 'success' });
            if (onSubmit) onSubmit();
          })
          .catch(() => {
            setSnackbar({ open: true, message: 'Failed to submit individual report', variant: 'error' });
          });
      }}
    >
      {() => (
        <Form>
          <CardContent style={{ paddingLeft: 8 }}>
            <Typography variant='subtitle2' gutterBottom>
              {'Your Individual Report'}
            </Typography>

            {allowCICO ? (
              <IndividualCICOSection disabled={readOnly} volunteerProfile={volunteerProfile} />
            ) : (
              <IndividualAttendanceSection disabled={readOnly} volunteerProfile={volunteerProfile} />
            )}

            {reportTypeFieldItems.map((reportTypeItem: any) => {
              if (reportTypeItem.__typename === 'VOLUNTEER_ReportTypeHeadingType') {
                return (
                  <Box key={reportTypeItem.reportTypeItemId} paddingTop={4} paddingBottom={1} display='inline-block'>
                    <Typography style={{ fontWeight: 600 }}>{reportTypeItem.heading}</Typography>
                  </Box>
                );
              }

              if (reportTypeItem.__typename === 'VOLUNTEER_ReportTypeFieldType') {
                const { field } = reportTypeItem;
                const formikFormName = `fields[${field.fieldId}].value`;
                const label = field.name;
                const required = !reportTypeItem.optional;

                return (
                  <SessionReportField
                    key={reportTypeItem.reportTypeItemId}
                    name={field.name}
                    description={field.description}
                    optional={reportTypeItem.optional}
                  >
                    <ReportTypeFieldInput
                      field={field}
                      formikFormName={formikFormName}
                      label={label}
                      required={required}
                      disabled={readOnly}
                    />
                  </SessionReportField>
                );
              }

              return null;
            })}
          </CardContent>
          <CardActions>
            <Box display='flex' width={1} justifyContent='flex-end' px={1} pb={1}>
              {readOnly ? (
                onEdit && (
                  <TabletButton
                    variant='outlined'
                    color='primary'
                    onClick={onEdit}
                    startIcon={<EditOutlined style={{ fontSize: 14 }} />}
                  >
                    {'Edit Report'}
                  </TabletButton>
                )
              ) : (
                <>
                  {onCancel && (
                    <TabletButton variant='text' color='default' onClick={onCancel} style={{ marginRight: 8 }}>
                      {'Cancel'}
                    </TabletButton>
                  )}
                  <TabletButton
                    variant='contained'
                    color='primary'
                    type='submit'
                    disabled={saveIndividualReport.isLoading}
                    startIcon={<Save />}
                  >
                    {'Submit'}
                  </TabletButton>
                </>
              )}
            </Box>
          </CardActions>
        </Form>
      )}
    </Formik>
  );
};

const IndividualAttendanceSection = ({
  disabled,
  volunteerProfile,
}: {
  disabled?: boolean;
  volunteerProfile?: VolunteerProfile;
}) => {
  return (
    <SessionReportField name='Attendance' description='Did you attend this session?'>
      <FormikField name='attended'>
        {({ field: formikField, form: { setFieldValue } }: FormikFieldProps) => {
          const isChecked = formikField.value;
          return (
            <ListItem
              button
              onClick={() => {
                if (!disabled) setFieldValue('attended', !isChecked);
              }}
              disabled={disabled}
            >
              <FormControlLabel
                {...formikField}
                control={<Checkbox checked={isChecked} color='primary' disabled={disabled} />}
                label={''}
              />
              <Avatar
                preferredName={volunteerProfile?.preferredName}
                lastName={volunteerProfile?.lastName}
                avatarUrl={volunteerProfile?.avatarUrl}
                size={35}
              />
              <Typography style={{ marginLeft: 8 }} color={isChecked ? 'textPrimary' : 'textSecondary'}>
                {`${volunteerProfile?.preferredName ?? ''} ${volunteerProfile?.lastName ?? ''}`}
              </Typography>
            </ListItem>
          );
        }}
      </FormikField>
    </SessionReportField>
  );
};

const IndividualCICOSection = ({ disabled }: { disabled?: boolean; volunteerProfile?: VolunteerProfile }) => {
  return (
    <SessionReportField name='Attendance' description='What time did you check in and out?'>
      <Box display='flex' alignItems='center' style={{ gap: 16 }}>
        <FormikField name='checkIn'>
          {({ field: formikField, form: { setFieldValue } }: FormikFieldProps) => (
            <TextField
              label='Check In'
              type='time'
              variant='outlined'
              size='small'
              value={formikField.value ? DateTime.fromJSDate(formikField.value).toFormat('HH:mm') : ''}
              onChange={(e) => {
                const parsed = DateTime.fromFormat(e.target.value, 'HH:mm');
                setFieldValue('checkIn', parsed.isValid ? parsed.toJSDate() : '');
              }}
              InputLabelProps={{ shrink: true }}
              disabled={disabled}
            />
          )}
        </FormikField>
        <FormikField name='checkOut'>
          {({ field: checkOutField, form: checkOutForm }: FormikFieldProps) => (
            <TextField
              label='Check Out'
              type='time'
              variant='outlined'
              size='small'
              value={checkOutField.value ? DateTime.fromJSDate(checkOutField.value).toFormat('HH:mm') : ''}
              onChange={(e) => {
                const parsed = DateTime.fromFormat(e.target.value, 'HH:mm');
                checkOutForm.setFieldValue('checkOut', parsed.isValid ? parsed.toJSDate() : '');
              }}
              InputLabelProps={{ shrink: true }}
              disabled={disabled}
            />
          )}
        </FormikField>
      </Box>
    </SessionReportField>
  );
};
